/**
 * Turn X API response errors into a short, actionable message.
 *
 * X's SDK message is often only "Request failed with code 402" even though
 * the response body identifies the actual problem. Keep this parser free of
 * SDK types so both the server publisher and the browser can use it for new
 * and legacy persisted errors.
 */

interface XErrorInput {
  code?: number | null;
  data?: unknown;
  message?: string;
}

function record(value: unknown): Record<string, unknown> | null {
  return value && typeof value === 'object' && !Array.isArray(value)
    ? (value as Record<string, unknown>)
    : null;
}

function text(value: unknown): string | null {
  return typeof value === 'string' && value.trim() ? value.trim() : null;
}

function statusLabel(code: number | null, title: string | null): string {
  if (code !== null && title) return `${code} ${title}`;
  if (code !== null) return String(code);
  return 'unknown status';
}

function dataFromMessage(message: string): unknown {
  const match = message.match(/:\s*(\{[\s\S]*\})\s*$/);
  if (!match?.[1]) return undefined;
  try {
    return JSON.parse(match[1]);
  } catch {
    return undefined;
  }
}

/** Format one structured X response or an SDK error message. */
export function formatXResponseError(input: XErrorInput): string {
  const message = text(input.message) ?? 'X API request failed';
  const data = record(input.data) ?? record(dataFromMessage(message));
  const code = typeof input.code === 'number' && Number.isFinite(input.code)
    ? input.code
    : (() => {
        const match = message.match(/code\s+(\d{3})/i);
        return match?.[1] ? Number(match[1]) : null;
      })();
  const detail = text(data?.detail);
  const title = text(data?.title);
  const type = text(data?.type);
  const creditsDepleted =
    code === 402 ||
    type?.endsWith('/credits-depleted') === true ||
    detail?.toLowerCase().includes('credits depleted') === true;

  if (creditsDepleted) {
    return `X API credits are depleted (${statusLabel(code ?? 402, title ?? 'Payment Required')}). Add credits in the X Developer Console, then retry publishing.`;
  }

  if (detail) return `X API request failed (${statusLabel(code, title)}): ${detail}`;

  const errors = Array.isArray(data?.errors) ? data.errors : [];
  const errorDetails = errors
    .map((entry) => {
      const item = record(entry);
      return text(item?.message) ?? text(item?.detail);
    })
    .filter((entry): entry is string => Boolean(entry));
  if (errorDetails.length > 0) {
    return `X API request failed (${statusLabel(code, title)}): ${errorDetails.join('; ')}`;
  }

  if (code !== null) return `X API request failed (${statusLabel(code, title)})`;
  return message;
}

/**
 * Format an error already stored in `articles.publish_error`.
 * Non-X validation or bookkeeping errors are returned unchanged.
 */
export function formatStoredPublishError(message: string): string {
  const trimmed = message.trim();
  // Persisted failures describe a past attempt, not the account's current
  // balance. Accept both legacy SDK messages and our previously formatted text.
  if (/request failed with code\s+402\b|\b402 Payment Required\b|credits (?:are )?depleted|credits-depleted/i.test(trimmed)) {
    return 'X rejected the last attempt for depleted credits (402 Payment Required). If you have topped up, retry publishing.';
  }
  if (!/request failed with code\s+\d{3}/i.test(trimmed) && !/credits depleted/i.test(trimmed)) {
    return message;
  }

  const codeMatch = trimmed.match(/code\s+(\d{3})/i);
  const data = dataFromMessage(trimmed);
  const formatted = formatXResponseError({
    code: codeMatch?.[1] ? Number(codeMatch[1]) : undefined,
    data,
    message: trimmed,
  });
  return formatted;
}
